import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { API_URL } from "../../config";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faChevronLeft,
  faChevronRight,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const Services = () => {
  const [services, setservices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRendering, setIsRendering] = useState(false);
  const token = localStorage.getItem("authToken");

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  // Search filter states
  const [serviceNameFilter, setServiceNameFilter] = useState("");
  const [exchangeFilter, setExchangeFilter] = useState("");
  const [quantityFilter, setQuantityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}service/active-services`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const allData = response.data.data;
      setTotal(allData.length);
      setPageCount(Math.ceil(allData.length / limit));
      setservices(allData);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    const handleClearFilters = () => {
      setServiceNameFilter("");
      setExchangeFilter("");
      setQuantityFilter("");
      setStatusFilter("");
    };

    window.addEventListener('clearServicesFiltersEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearServicesFiltersEvent', handleClearFilters);
    };
  }, []);

  // Filter services based on search criteria
  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      // Service Name filter
      if (serviceNameFilter &&
        !service.service_name.toLowerCase().includes(serviceNameFilter.toLowerCase())) {
        return false;
      }

      // Exchange filter
      if (exchangeFilter) {
        let exchange = "";
        if (service.check_nse) exchange += "NSE - ";
        if (service.check_bse) exchange += "BSE - ";
        if (service.check_mcx) exchange += "MCX - ";
        if (service.check_nfo) exchange += "NFO - ";
        if (service.check_bfo) exchange += "BFO - ";

        if (exchange.endsWith(" - ")) {
          exchange = exchange.slice(0, -3);
        }

        if (!exchange.toLowerCase().includes(exchangeFilter.toLowerCase())) {
          return false;
        }
      }

      // Quantity filter
      if (quantityFilter) {
        const lotContent = service.lot_alloc
          ? "Based On Lot Size"
          : `${service.qty_min} / CMP`;

        if (!lotContent.toLowerCase().includes(quantityFilter.toLowerCase())) {
          return false;
        }
      }

      // Status filter
      if (statusFilter && statusFilter !== "all") {
        const status = service.activate == 1 ? "active" : "inactive";
        if (status !== statusFilter.toLowerCase()) {
          return false;
        }
      }

      return true;
    });
  }, [services, serviceNameFilter, exchangeFilter, quantityFilter, statusFilter]);

  // Update pagination based on filtered results
  useEffect(() => {
    setTotal(filteredServices.length);
    setPageCount(Math.ceil(filteredServices.length / limit));
    setPage(1); // Reset to first page when filters change
  }, [filteredServices, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };
  useEffect(() => {
    setIsRendering(true);

    const timer = setTimeout(() => {
      setIsRendering(false);
    }, 300); // smooth UX (same as report page)

    return () => clearTimeout(timer);
  }, [serviceNameFilter, exchangeFilter, quantityFilter, statusFilter]);

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedServices = filteredServices.slice(start - 1, end);

  // if (loading) {
  //   return <LoadingSpinner />
  // } 

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        {/* Alert Box Placeholder */}
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">Manage Services</h4>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            <PermissionWrapper module="services" action="add">
              <Link
                to={"/research/Services/credit/add"}
                className="btn btn-default btn-sm me-2"
              >
                <FontAwesomeIcon icon={faPlus} /> Add Credit Based Service
              </Link>
            </PermissionWrapper>
            {/* <Link
              to={"/research/Services/portfolio/add"}
              className="btn btn-default btn-sm"
            >
              <FontAwesomeIcon icon={faPlus} /> Add Portfolio Based Service
            </Link> */}

            {/* 🔽 Pagination Controls after buttons */}
            <div className="d-inline-block ms-3">
              <span style={{ fontSize: "0.8rem" }} className="me-2">
                {start} to {end} of {total}
              </span>
              <button
                className="btn btn-default btn-sm"
                title="Previous"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className="btn btn-default btn-sm"
                title="Next"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === pageCount}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          <PermissionWrapper module="services" action="view">
            {loading || services.length > 0 ? (
              <div className="service-table">
                <table className="table table-condensed table-bordered bg-white">
                  <thead>
                    <tr>
                      <th style={{ textAlign: "center" }}>#</th>
                      <th>Image</th>
                      <th>Service Name</th>
                      <th>Exchange</th>
                      <th>Quantity</th>
                      <th>Status</th>
                      <th>Edit</th>
                    </tr>
                    <tr>
                      <th style={{ textAlign: "center" }}></th>
                      <th></th>
                      <th>
                        <div className="input-group input-group-sm">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search name..."
                            value={serviceNameFilter}
                            onChange={(e) => setServiceNameFilter(e.target.value)}
                          />
                          {/* <span className="input-group-addon">
                          <FontAwesomeIcon icon={faSearch} />
                        </span> */}
                        </div>
                      </th>
                      <th>
                        <div className="input-group input-group-sm">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search exchange..."
                            value={exchangeFilter}
                            onChange={(e) => setExchangeFilter(e.target.value)}
                          />
                          {/* <span className="input-group-addon">
                          <FontAwesomeIcon icon={faSearch} />
                        </span> */}
                        </div>
                      </th>
                      <th>
                        <div className="input-group input-group-sm">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search quantity..."
                            value={quantityFilter}
                            onChange={(e) => setQuantityFilter(e.target.value)}
                          />
                          {/* <span className="input-group-addon">
                          <FontAwesomeIcon icon={faSearch} />
                        </span> */}
                        </div>
                      </th>
                      <th>
                        <div className="input-group input-group-sm">
                          <select
                            className="form-control"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                          >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      </th>
                      <th>
                        <button
                          className="btn btn-default btn-sm"
                          onClick={() => {
                            setServiceNameFilter("");
                            setExchangeFilter("");
                            setQuantityFilter("");
                            setStatusFilter("all");
                          }}
                          title="Clear filters"
                        >
                          Clear
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(loading || isRendering) ? (
                      Array.from({ length: 10 }).map((_, rowIndex) => (
                        <tr key={`skeleton-${rowIndex}`} className="skeleton-row">
                          {Array.from({ length: 7 }).map((_, colIndex) => (
                            <td key={colIndex}>
                              <div className="skeleton-box"></div>
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : paginatedServices.length > 0 ? (
                      paginatedServices.map((post, index) => {
                        let exchange = "";
                        if (post.check_nse) exchange += "NSE - ";
                        if (post.check_bse) exchange += "BSE - ";
                        if (post.check_mcx) exchange += "MCX - ";
                        if (post.check_nfo) exchange += "NFO - ";
                        if (post.check_bfo) exchange += "BFO - ";

                        if (exchange.endsWith(" - ")) {
                          exchange = exchange.slice(0, -3);
                        }

                        const lotContent = post.lot_alloc
                          ? "Based On Lot Size"
                          : `${post.qty_min} / CMP`;

                        return (
                          <tr key={post.id}>
                            <td style={{ textAlign: "center" }}>
                              {(page - 1) * limit + index + 1}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {post.service_image ? (
                                <img
                                  src={post.service_image}
                                  alt="service img"
                                  style={{ width: 24, height: 24, objectFit: "contain" }}
                                />
                              ) : (
                                <span></span>
                              )}
                            </td>
                            <td>{post.service_name}</td>
                            <td>{exchange}</td>
                            <td>{lotContent}</td>
                            <td>{post.activate == 1 ? "Active" : "Inactive"}</td>
                            <td>
                              <PermissionWrapper module="services" action="edit">
                                {post.service_type == 0 ? (
                                  <Link
                                    to={`/research/Services/credit/edit/${post.id}`}
                                    className="mx-2 btn btn-warning btn-xs"
                                    style={{ float: "left" }}
                                    data-toggle="tooltip"
                                    data-placement="bottom"
                                    title="Edit Credit Service"
                                  >
                                    <i className="ti ti-pencil"></i>
                                  </Link>
                                ) : (
                                  <Link
                                    to={`/research/Services/portfolio/edit/${post.id}`}
                                    className="mx-2 btn btn-warning btn-xs"
                                    style={{ float: "left" }}
                                    data-toggle="tooltip"
                                    data-placement="bottom"
                                    title="Edit Portfolio Service"
                                  >
                                    <i className="ti ti-pencil"></i>
                                  </Link>
                                )}
                              </PermissionWrapper>

                              <Link
                                to={`/research/Services/credit/${post.id}`}
                                className="btn btn-info btn-xs"
                                style={{ float: "left" }}
                                data-toggle="tooltip"
                                data-placement="bottom"
                                title="View Report"
                              >
                                <i className="ti ti-book"></i>
                              </Link>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center">
                          No records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div>No records found.</div>
            )}
          </PermissionWrapper>
        </div>
      </div>

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default Services;